import { addMinutes, format, parse, parseISO } from 'date-fns';
import { drop, dropLast, isEmpty, join, keys, values } from 'ramda';

import ooe from '../constants';

export function formatPrice(price?: number | string) {
  if (Number.isNaN(price) || !price) {
    return '$0.00';
  }
  return `$${parseFloat(price.toString()).toFixed(2)}`;
}

export function formatModifierPrice(price = 0.0) {
  if (price === 0.0 || Number.isNaN(price)) {
    return 'Free';
  }
  return `$${parseFloat(price.toString()).toFixed(2)}`;
}

export function formatItemName(name?: string) {
  if (!name) {
    return '';
  }
  const replacementMap: Record<string, string> = {
    '<sup>™</sup>': '™',
    '<sup>®</sup>': '®',
  };
  return keys(replacementMap).reduce((acc, key) => {
    const replaceWith = replacementMap[key];
    const re = new RegExp(key, 'gi');
    return acc.replace(re, replaceWith);
  }, name);
}

export function formatModifier(mod: { action?: string; modifierType?: string; name?: string }) {
  const { action, modifierType, name } = mod;
  if (!name || typeof name !== 'string') {
    return '';
  }
  if (action === 'REMOVE' || (modifierType && ooe.DEFAULT_AVAILABLE_MODIFIER_TYPES.includes(modifierType))) {
    return `No ${name}`;
  }
  return `${formatItemName(name)}`;
}

export function formatEventSummaryDate(date: Date) {
  return format(date, ooe.DATE_TIME_FORMAT.eventSummaryDate);
}

export function formatEventSummaryTime(time: string, isDelivery?: boolean) {
  return formatTimePickerTime(time, isDelivery);
}

export function formatTimePickerTime(time: string, isDelivery?: boolean) {
  return (
    format(parse(time, ooe.DATE_TIME_FORMAT.time, new Date()), ooe.DATE_TIME_FORMAT.eventSummaryTime) +
    (isDelivery
      ? ' - ' +
        format(
          addMinutes(parse(time, ooe.DATE_TIME_FORMAT.time, new Date()), ooe.TIME_SLOT_INTERVAL_IN_MIN),
          ooe.DATE_TIME_FORMAT.eventSummaryTime,
        )
      : '')
  );
}

export function formatLongDate(dateTime: string) {
  return format(parseISO(dateTime), ooe.DATE_TIME_FORMAT.longDate);
}

export function formatShortDate(dateTime: string) {
  return format(parseISO(dateTime), ooe.DATE_TIME_FORMAT.shortDate);
}

export function formatPaper(paperGoods: boolean) {
  return paperGoods
    ? 'Please include plates, cutlery, napkins and cups. '
    : 'Please do not include plates, cutlery, napkins or cups. ';
}

export function formatPaperGoodsOptions(paperGoods: Record<string, boolean>) {
  if (isEmpty(paperGoods)) {
    return '';
  }
  if (!isEmpty(paperGoods) && Object.values(paperGoods).some((opt) => opt === true)) {
    const paperGoodsArr = Object.keys(paperGoods).filter((k) => paperGoods[k]);
    if (paperGoodsArr.length === 2) {
      paperGoodsArr.splice(1, 0, ' and ');
    } else if (paperGoodsArr.length === 3) {
      paperGoodsArr.splice(1, 0, ', ');
      paperGoodsArr.splice(-1, 0, ' and ');
    } else if (paperGoodsArr.length === 4) {
      paperGoodsArr.splice(1, 0, ', ');
      paperGoodsArr.splice(3, 0, ', ');
      paperGoodsArr.splice(-1, 0, ' and ');
    }
    return `Please include ${paperGoodsArr.join('')}.`;
  }
  return 'Please do not include plates, cutlery, napkins or cups.';
}

export function formatDeliveryAddress(address: Record<string, string | null>) {
  return join(' ')(drop(1, dropLast(1, values(address))));
}

export function validateEmail(email: string) {
  // Email validation RegEx found on SO.
  const re =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function capitalizeFirstLetter(input?: string) {
  if (!input) {
    return '';
  }
  const [first, ...rest] = input;
  return first ? first.toUpperCase() + rest.join('') : '';
}

export function titleCase(string = '') {
  if (!string || typeof string !== 'string') {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1);
}

export const formatPhoneNumber = (phone: string) => {
  if (phone && phone[0] !== '+') {
    return `+1 ${phone}`;
  }
  return phone;
};
